// src/pages/admin/Invoices.js
import React, { useState } from "react";
import {
  Typography,
  Box,
  Card,
  CardContent,
  Grid,
  TextField,
  InputAdornment,
  Button,
  Chip,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  IconButton,
  Menu,
  MenuItem,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  useTheme,
  alpha,
  Avatar
} from "@mui/material";
import {
  Search as SearchIcon,
  FilterList as FilterIcon,
  MoreVert as MoreIcon,
  Receipt as InvoiceIcon,
  Download as DownloadIcon,
  Email as EmailIcon,
  Print as PrintIcon,
  Visibility as ViewIcon,
  CheckCircle as PaidIcon,
  Pending as PendingIcon,
  Schedule as OverdueIcon,
  Person as CustomerIcon,
} from "@mui/icons-material";

// Mock data for invoices
const mockInvoices = [
  {
    id: "INV-001",
    customer: {
      name: "Sarah Johnson",
      email: "sarah.j@example.com",
      avatar: ""
    },
    amount: 189,
    date: "2024-03-20",
    dueDate: "2024-03-27",
    status: "paid",
    services: ["Deep Cleaning"],
    period: "Mar 20, 2024",
    paymentMethod: "credit_card"
  },
  {
    id: "INV-002",
    customer: {
      name: "Mike Brown",
      email: "mike.b@example.com",
      avatar: ""
    },
    amount: 89,
    date: "2024-03-21",
    dueDate: "2024-03-28",
    status: "pending",
    services: ["Standard Cleaning"],
    period: "Mar 21, 2024",
    paymentMethod: "paypal"
  },
  {
    id: "INV-003",
    customer: {
      name: "Emily Davis",
      email: "emily.d@example.com",
      avatar: ""
    },
    amount: 249,
    date: "2024-03-19",
    dueDate: "2024-03-26",
    status: "overdue",
    services: ["Move-In Cleaning"],
    period: "Mar 19, 2024",
    paymentMethod: "bank_transfer"
  },
  {
    id: "INV-004",
    customer: {
      name: "David Wilson",
      email: "david.w@example.com",
      avatar: ""
    },
    amount: 149,
    date: "2024-03-22",
    dueDate: "2024-03-29",
    status: "paid",
    services: ["Office Cleaning"],
    period: "Mar 22, 2024",
    paymentMethod: "credit_card"
  },
  {
    id: "INV-005",
    customer: {
      name: "Jennifer Lopez",
      email: "jennifer.l@example.com",
      avatar: ""
    },
    amount: 89,
    date: "2024-03-18",
    dueDate: "2024-03-25",
    status: "pending",
    services: ["Standard Cleaning"],
    period: "Mar 18, 2024",
    paymentMethod: "paypal"
  }
];

const StatusChip = ({ status }) => {
  const getStatusConfig = (status) => {
    switch (status) {
      case 'paid':
        return { color: 'success', label: 'Paid', icon: <PaidIcon /> };
      case 'pending':
        return { color: 'warning', label: 'Pending', icon: <PendingIcon /> };
      case 'overdue':
        return { color: 'error', label: 'Overdue', icon: <OverdueIcon /> };
      default:
        return { color: 'default', label: status, icon: null };
    }
  };

  const config = getStatusConfig(status);

  return (
    <Chip
      label={config.label}
      color={config.color}
      size="small"
      icon={config.icon}
    />
  );
};

export default function Invoices() {
  const theme = useTheme();
  const [searchTerm, setSearchTerm] = useState("");
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [filterStatus, setFilterStatus] = useState('all');
  const [anchorEl, setAnchorEl] = useState(null);
  const [selectedInvoice, setSelectedInvoice] = useState(null);

  const filteredInvoices = mockInvoices.filter(invoice =>
    invoice.customer.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    invoice.id.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const stats = [
    { label: 'Total Invoices', value: '156', change: '+12%' },
    { label: 'Paid', value: '$12,540', change: '+8%' },
    { label: 'Pending', value: '$2,280', change: '+15%' },
    { label: 'Overdue', value: '$890', change: '-5%' },
  ];

  const totalRevenue = mockInvoices
    .filter(inv => inv.status === 'paid')
    .reduce((sum, inv) => sum + inv.amount, 0);

  const pendingAmount = mockInvoices
    .filter(inv => inv.status === 'pending')
    .reduce((sum, inv) => sum + inv.amount, 0);

  const overdueAmount = mockInvoices
    .filter(inv => inv.status === 'overdue')
    .reduce((sum, inv) => sum + inv.amount, 0);

  return (
    <Box>
      {/* Header */}
      <Box sx={{ mb: 4 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
          <Typography variant="h4" fontWeight="bold">
            Invoice Management
          </Typography>
          <Button
            variant="contained"
            startIcon={<InvoiceIcon />}
          >
            Create Invoice
          </Button>
        </Box>
        <Typography variant="body1" color="text.secondary">
          Manage customer invoices, track payments, and send reminders
        </Typography>
      </Box>

      {/* Stats */}
      <Grid container spacing={3} sx={{ mb: 4 }}>
        <Grid item xs={12} md={3}>
          <Card>
            <CardContent>
              <Typography variant="h4" fontWeight="bold" color="primary" gutterBottom>
                {mockInvoices.length}
              </Typography>
              <Typography variant="body2" color="text.secondary" gutterBottom>
                Total Invoices
              </Typography>
              <Chip label="+12%" size="small" color="success" variant="outlined" />
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} md={3}>
          <Card>
            <CardContent>
              <Typography variant="h4" fontWeight="bold" color="success.main" gutterBottom>
                ${totalRevenue}
              </Typography>
              <Typography variant="body2" color="text.secondary" gutterBottom>
                Paid Amount
              </Typography>
              <Chip label="+8%" size="small" color="success" variant="outlined" />
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} md={3}>
          <Card>
            <CardContent>
              <Typography variant="h4" fontWeight="bold" color="warning.main" gutterBottom>
                ${pendingAmount}
              </Typography>
              <Typography variant="body2" color="text.secondary" gutterBottom>
                Pending Amount
              </Typography>
              <Chip label="+15%" size="small" color="warning" variant="outlined" />
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} md={3}>
          <Card>
            <CardContent>
              <Typography variant="h4" fontWeight="bold" color="error.main" gutterBottom>
                ${overdueAmount}
              </Typography>
              <Typography variant="body2" color="text.secondary" gutterBottom>
                Overdue Amount
              </Typography>
              <Chip label="-5%" size="small" color="error" variant="outlined" />
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Controls */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Grid container spacing={2} alignItems="center">
            <Grid item xs={12} md={4}>
              <TextField
                placeholder="Search invoices..."
                variant="outlined"
                size="small"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                fullWidth
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon color="action" />
                    </InputAdornment>
                  ),
                }}
              />
            </Grid>
            <Grid item xs={12} md={2}>
              <Button
                variant="outlined"
                startIcon={<FilterIcon />}
                fullWidth
              >
                Filters
              </Button>
            </Grid>
            <Grid item xs={12} md={2}>
              <Button
                variant="outlined"
                startIcon={<DownloadIcon />}
                fullWidth
              >
                Export
              </Button>
            </Grid>
            <Grid item xs={12} md={2}>
              <Button
                variant="outlined"
                startIcon={<EmailIcon />}
                fullWidth
              >
                Send Reminders
              </Button>
            </Grid>
            <Grid item xs={12} md={2}>
              <Button
                variant="outlined"
                startIcon={<PrintIcon />}
                fullWidth
              >
                Bulk Print
              </Button>
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      {/* Invoices Table */}
      <Card>
        <TableContainer>
          <Table>
            <TableHead sx={{ bgcolor: alpha(theme.palette.primary.main, 0.04) }}>
              <TableRow>
                <TableCell>Invoice</TableCell>
                <TableCell>Customer</TableCell>
                <TableCell>Amount</TableCell>
                <TableCell>Date</TableCell>
                <TableCell>Due Date</TableCell>
                <TableCell>Services</TableCell>
                <TableCell>Payment Method</TableCell>
                <TableCell>Status</TableCell>
                <TableCell>Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {filteredInvoices
                .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                .map((invoice) => (
                  <TableRow key={invoice.id} hover sx={{ cursor: 'pointer' }}>
                    <TableCell>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <InvoiceIcon color="primary" />
                        <Box>
                          <Typography variant="subtitle1" fontWeight="600">
                            {invoice.id}
                          </Typography>
                          <Typography variant="body2" color="text.secondary">
                            {invoice.period}
                          </Typography>
                        </Box>
                      </Box>
                    </TableCell>
                    <TableCell>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                        <Avatar src={invoice.customer.avatar}>
                          <CustomerIcon />
                        </Avatar>
                        <Box>
                          <Typography variant="subtitle1" fontWeight="600">
                            {invoice.customer.name}
                          </Typography>
                          <Typography variant="body2" color="text.secondary">
                            {invoice.customer.email}
                          </Typography>
                        </Box>
                      </Box>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body1" fontWeight="600" color="primary">
                        ${invoice.amount}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2">
                        {new Date(invoice.date).toLocaleDateString()}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography 
                        variant="body2" 
                        color={invoice.status === 'overdue' ? 'error.main' : 'text.secondary'}
                        fontWeight={invoice.status === 'overdue' ? 600 : 400}
                      >
                        {new Date(invoice.dueDate).toLocaleDateString()}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, maxWidth: 200 }}>
                        {invoice.services.map((service, index) => (
                          <Chip key={index} label={service} size="small" variant="outlined" />
                        ))}
                      </Box>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2" color="text.secondary">
                        {invoice.paymentMethod === 'credit_card' ? 'Credit Card' : 
                         invoice.paymentMethod === 'paypal' ? 'PayPal' : 'Bank Transfer'}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <StatusChip status={invoice.status} />
                    </TableCell>
                    <TableCell>
                      <IconButton size="small">
                        <ViewIcon />
                      </IconButton>
                      <IconButton size="small">
                        <EmailIcon />
                      </IconButton>
                      <IconButton size="small">
                        <MoreIcon />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                ))}
            </TableBody>
          </Table>
        </TableContainer>
        <TablePagination
          rowsPerPageOptions={[5, 10, 25]}
          component="div"
          count={filteredInvoices.length}
          rowsPerPage={rowsPerPage}
          page={page}
          onPageChange={(e, newPage) => setPage(newPage)}
          onRowsPerPageChange={(e) => {
            setRowsPerPage(parseInt(e.target.value, 10));
            setPage(0);
          }}
        />
      </Card>
    </Box>
  );
}